// Pure helpers for rendering and serialising a capture document. Shared by
// the Captures table (bulk copy / download), CaptureRow (single-row copy) and
// CaptureDetail (the expanded panel) so the body decoding, URL reconstruction
// and the copied/downloaded JSON shape stay identical everywhere.

// headerVal pulls a header value case-insensitively. Headers come back
// canonicalised from the server, but be lenient. Returns '' when absent
// rather than 'undefined'.
export function headerVal(c, key) {
  const h = c.headers || {};
  const wanted = key.toLowerCase();
  for (const k of Object.keys(h)) {
    if (k.toLowerCase() === wanted) {
      const v = h[k];
      return Array.isArray(v) ? v[0] : v;
    }
  }
  return '';
}

// decodedBody returns the request body as text, decoding base64 when the
// capture stored a binary/non-UTF8 payload.
export function decodedBody(c) {
  if (!c.body) return '';
  if (c.body_base64) {
    try { return atob(c.body); } catch { return c.body; }
  }
  return c.body;
}

// prettyBody pretty-prints JSON bodies; everything else is returned verbatim.
export function prettyBody(c) {
  const raw = decodedBody(c);
  if (!raw) return '';
  const ct = (c.content_type || '').toLowerCase();
  if (ct.includes('json')) {
    try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
  }
  return raw;
}

// isJsonBody detects JSON from the declared content-type or by sniffing the
// payload -- some senders POST JSON with text/plain or no header at all.
export function isJsonBody(c) {
  const ct = (c.content_type || '').toLowerCase();
  if (ct.includes('json')) return true;
  const raw = decodedBody(c).trimStart();
  if (!raw) return false;
  const ch = raw[0];
  if (ch !== '{' && ch !== '[') return false;
  try { JSON.parse(raw); return true; } catch { return false; }
}

// reconstructUrl rebuilds the URL as the caller saw it: X-Forwarded-* behind a
// proxy, the plain Host header otherwise, falling back gracefully per field.
export function reconstructUrl(c) {
  const proto  = headerVal(c, 'X-Forwarded-Proto') || 'http';
  const host   = headerVal(c, 'X-Forwarded-Host') || headerVal(c, 'Host') || 'localhost';
  const bucket = c.bucket_id ? `/h/${c.bucket_id}` : '/h';
  const q = c.query ? `?${c.query}` : '';
  return `${proto}://${host}${bucket}${c.path || ''}${q}`;
}

// parsedQuery yields [key, value] pairs with percent-decoded values.
export function parsedQuery(c) {
  if (!c.query) return [];
  const out = [];
  for (const [k, v] of new URLSearchParams(c.query)) out.push([k, v]);
  return out;
}

export function requestLine(c) {
  const q = c.query ? `?${c.query}` : '';
  return `${c.method} ${c.path || '/'}${q} ${c.proto || ''}`.trim();
}

// capturePayload is the self-contained object copied to the clipboard or
// written to a download -- a teammate pasting it into Slack shouldn't need
// portal access to understand the request. query_params is the decoded
// convenience view; full_url reconstructs the original URL from headers.
export function capturePayload(c) {
  return {
    id:              c.id,
    bucket_id:       c.bucket_id || '',
    method:          c.method,
    path:            c.path,
    query:           c.query || '',
    query_params:    Object.fromEntries(parsedQuery(c)),
    full_url:        reconstructUrl(c),
    proto:           c.proto || '',
    headers:         c.headers || {},
    content_type:    c.content_type || '',
    body:            decodedBody(c),
    body_base64:     !!c.body_base64,
    body_size:       c.body_size,
    body_truncated:  !!c.body_truncated,
    remote_addr:     c.remote_addr || '',
    user_agent:      c.user_agent || '',
    subject:         c.subject || '',
    received:        c.created_at,
  };
}
